/**
 * officePage - an Office page whose code is fetched with its siblings and,
 * once here, draws with no loading flash (warmtabs).
 *
 * Every route is `React.lazy` inside its own `<Suspense>`. A boundary that is
 * new on screen always shows its fallback while the lazy part resolves, so the
 * first hop to each Office tab (Board, CRM, Client Requests, Partner Program)
 * blanked the content into the grey skeleton, and even a chunk already in the
 * browser cache costs one fallback frame through `lazy`. The founder read that
 * as "loading from a new thing with every click".
 *
 * Two changes, nothing else about the route moves:
 *
 *  - Once a page's module has arrived, it is rendered directly, not through
 *    `lazy`, so there is nothing left to suspend on.
 *  - Opening any Office page fetches the code of the others in the background,
 *    so the next tab is already here when it is clicked.
 */
import { lazy, useEffect, useState, type ComponentType } from 'react';

type PageModule = { default: ComponentType };

const siblings: (() => void)[] = [];

/** Fetch the code of every Office page that is not here yet. */
function preloadOffice(): void {
    for (const preload of siblings) preload();
}

export function officePage(factory: () => Promise<PageModule>): ComponentType {
    let loaded: ComponentType | null = null;
    let pending: Promise<PageModule> | null = null;
    const fetchIt = () => (pending ??= factory().then(
        (m) => { loaded = m.default; return m; },
        // A failed fetch may be tried again (a stale chunk after a deploy is
        // RouteError's to reload, as for every other route).
        (e: unknown) => { pending = null; throw e; },
    ));
    const Lazy = lazy(fetchIt);
    siblings.push(() => { void fetchIt().catch(() => { /* the click will say so */ }); });

    return function OfficePage() {
        // Chosen once per mount: swapping `Lazy` for the module mid-life would
        // be a different component type, and remount the page.
        const [Page] = useState<ComponentType>(() => loaded ?? Lazy);
        useEffect(() => {
            const idle = (window as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
            if (idle) idle(preloadOffice); else setTimeout(preloadOffice, 300);
        }, []);
        return <Page />;
    };
}
